import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faClipboardCheck } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { CheckCircle2, Loader2 as Loader2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { useTheme } from '@/contexts/ThemeContext';
import { Timestamp } from 'firebase/firestore';
import { db } from '@/js/external-js/config/main_config';
import { getMenuSettings } from '@/services/menusService';
import { RiskRegister } from '@/js/external-js/variableModules/namespaces/RiskRegister.namespace';

// Add window property types for TS
declare global {
  interface Window {
    assignees?: any[];
    currentUser?: { userID: string;[key: string]: any };
  }
}

interface ReviewRiskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEvent?: {
    title?: string;
    description?: string;
    date?: string;
    id?: string;
    prevReviewDate?: string;
    reviewDate?: string;
    nextReviewDate?: string;
  };
  onDataChanged?: () => void;
}

// Default risk responses if menus service is not available
const defaultRiskResponses = [
  { label: 'Treat', value: 'treat' },
  { label: 'Tolerate', value: 'tolerate' },
  { label: 'Transfer', value: 'transfer' },
  { label: 'Terminate', value: 'terminate' },
];

const ReviewRiskDialog: React.FC<ReviewRiskDialogProps> = ({ open, onOpenChange, selectedEvent, onDataChanged }) => {
  const [riskResponses, setRiskResponses] = useState<{ label: string, value: string }[]>(defaultRiskResponses);

  // Load risk responses from menus service
  useEffect(() => {
    const loadRiskResponses = async () => {
      try {
        const menuSettings = await RiskRegister.Risk.Menu(window.companyID);
        console.log('[ReviewRiskDialog] Got menuSettings:', menuSettings);
        const responses = menuSettings.response;
        if (responses.length > 0) {
          setRiskResponses(
            responses.map((option: string) => ({
              label: option.charAt(0).toUpperCase() + option.slice(1).toLowerCase(),
              value: option.toLowerCase()
            }))
          );
        }
      } catch (error) {
        console.error('Failed to load risk responses:', error);
        // Keep default responses if loading fails
      }
    };

    if (open) {
      loadRiskResponses();
    }
  }, [open]);

  // Reset all dialog state when dialog opens or selectedEvent changes
  useEffect(() => {
    if (!open) return;
    setReviewedBy('');
    setRiskResponse('accept');
    setTreatmentPlan('');
    setComments('');
    setReviewDate(new Date());
    setNextReviewDate(new Date(new Date().setMonth(new Date().getMonth() + 12)));
    setUsers([]);
    // Add any other state resets as needed
  }, [open, selectedEvent]);
  const { isDark } = useTheme();
  const [reviewedBy, setReviewedBy] = useState('');
  const [riskResponse, setRiskResponse] = useState('accept');
  const [treatmentPlan, setTreatmentPlan] = useState('');
  const [comments, setComments] = useState('');
  const [users, setUsers] = useState<{ label: string; value: string }[]>([]);
  const [reviewDate, setReviewDate] = useState<Date | undefined>(
    new Date()
  );
  const [nextReviewDate, setNextReviewDate] = useState<Date | undefined>(
    new Date(new Date().setMonth(new Date().getMonth() + 12))
  );

  useEffect(() => {
    async function fetchUsers() {
      // Match PolicyAdmin: allow for async, robust fallback, and label/value fields
      let usersArr: any[] = [];
      if (window.assignees && Array.isArray(window.assignees)) {
        usersArr = window.assignees;
      }
      // fallback: try fetch from API if needed (future-proofing, not active here)
      const formatted = usersArr.map((user: any) => ({
        label: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email || user.userID,
        value: user.userID,
      }));
      setUsers(formatted);
      // Default to current user if available, else first user
      if (window.currentUser && window.currentUser.userID) {
        setReviewedBy(window.currentUser.userID);
      } else if (usersArr.length > 0) {
        setReviewedBy(usersArr[0].userID);
      }
    }
    if (open) fetchUsers();
  }, [open]);

  const handleCompleteReview = async () => {
    try {
      // Start loading state
      console.log('[RiskReview] Saving risk review...');



      const riskId = selectedEvent?.id;
      if (!riskId) throw new Error('No risk ID provided.');
      let riskData = await RiskRegister.RiskProxy.Get(riskId);
      riskData = riskData.data;


      if (!riskData) throw new Error('No risk data found for given ID.');

      // Fetch existing Firestore risk document
      const riskDocRef = db.collection('customers').doc(window.companyID).collection('compliance').doc('risk');
      const riskDoc = await riskDocRef.get();

      let versionSubNumber = 0;
      let versionNumber = 1;
      let versionKeys = [];

      if (riskDoc.exists && riskDoc.data()?.version) {
        versionKeys = Object.keys(riskDoc.data()?.version);
        const lastVersionKey = versionKeys[versionKeys.length - 1];
        const versionInfo = riskDoc.data()?.version?.[lastVersionKey];
        versionSubNumber = parseInt(versionInfo?.versionNumber?.split('.')[1] || '0');
        versionNumber = parseInt(versionInfo?.versionNumber?.split('.')[0] || '1');
      }

      // Build new version string
      const newVersion = autoIncrementVersion(riskData.version || '1.0') || '1.0';
      const versionLabel = isNaN(versionSubNumber)
        ? `${versionNumber}.0`
        : versionSubNumber < 9
          ? `${versionNumber}.${versionSubNumber + 1}`
          : `${versionNumber + 1}.0`;

      // Historical log of what changed (edit history)
      const riskHistory = Array.isArray(riskData?.history) ? [...riskData.history] : [];
      riskHistory.push({
        dateChanged: Timestamp.now(),
        changedBy: window.currentUser?.userID || '',
        version: newVersion,
        riskNew: {
          lastReviewDate: reviewDate || '',
          nextReviewDate: nextReviewDate || '',
          treatmentPlan: riskResponse === 'treat' ? treatmentPlan : '',
          response: riskResponse,
          version: newVersion
        }
      });
      
      // Build review history entry (for the review event itself)
      const newReviewHistoryEntry = {
        reviewDate: Timestamp.now(),
        reviewedBy: window.currentUser?.userID || '',
        scheduledDate: nextReviewDate ? Timestamp.fromDate(nextReviewDate) : null,
        lastReviewDate: reviewDate ? Timestamp.fromDate(reviewDate) : null,
        nextReviewDate: nextReviewDate ? Timestamp.fromDate(nextReviewDate) : null,
        comments: comments || '',
        riskRating: (riskData as any).riskRating || '',
        treatmentRequired: riskResponse === 'treat' ? 'Yes' : 'No',
      };
      
      // Append to reviewHistory (ensure it's an array)
      const reviewHistory: any[] = Array.isArray((riskData as any)?.reviewHistory)
        ? [...(riskData as any).reviewHistory, newReviewHistoryEntry]
        : [newReviewHistoryEntry];

      // Build new risk object
      const updatedRisk: any = {
        ...riskData,
        lastReviewDate: reviewDate || '',
        nextReviewDate: nextReviewDate || '',
        treatmentPlan: riskResponse === 'treat' ? treatmentPlan : '',
        response: riskResponse,
        version: newVersion,
        reviewHistory,
      };

      // Save updated risk
      await RiskRegister.RiskProxy.Edit(riskId, updatedRisk);

      // Create snapshot for global riskHistory array
      // Strip reviewHistory and history from updatedRisk for the snapshot
      const { reviewHistory: _rev, history: _hist, ...rest } = updatedRisk;

      // Locate the correct document in the 'register' collection to get its Firestore document ID
      let entryDoc;
      const riskRegisterRef = db
        .collection('customers')
        .doc(window.companyID)
        .collection('compliance')
        .doc('risk');

      if (isNaN(parseInt(riskId))) {
        // If riskId is not a number, assume it's a componentId
        const componentIdQuery = riskRegisterRef.collection("register").where("componentId", "==", riskId);
        entryDoc = await componentIdQuery.limit(1).get();
      } else {
        // Try by numeric riskID
        const riskIdQuery = riskRegisterRef.collection("register").where("riskID", "==", parseInt(riskId));
        entryDoc = await riskIdQuery.limit(1).get();

        // Fallback to componentId if no match
        if (entryDoc.empty) {
          const componentIdQuery = riskRegisterRef.collection("register").where("componentId", "==", riskId);
          entryDoc = await componentIdQuery.limit(1).get();
        }
      }

      if (entryDoc.empty) {
        throw new Error(`No register entry found for ID: ${riskId}`);
      }

      const documentId = entryDoc.docs[0].id; // Firestore document ID

      // Push snapshot to riskHistory collection (as a new doc)
      const docRef = await db
        .collection('customers')
        .doc(window.companyID)
        .collection('compliance')
        .doc('risk')
        .collection('riskHistory')
        .add({
          changedBy: window.currentUser?.userID || '',
          dateChanged: Timestamp.now(),
          riskNew: rest,
          version: newVersion,
          comments: comments,
          riskID: riskId,
          docID: documentId
        });

      console.log(`[RiskReview] ✅ Risk history entry added: ${docRef.id}`);
      console.log('[RiskReview] ✅ Risk updated successfully:', updatedRisk);
      if (typeof onDataChanged === 'function') onDataChanged();
      onOpenChange(false);
    } catch (err) {
      console.error('[RiskReview] ❌ Failed to save risk review:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl! w-full max-h-[90vh] overflow-hidden rounded-2xl border border-border bg-card sm:p-0! shadow-2xl [&>button]:hidden p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col h-full max-h-[90vh] p-0">
          {/* Close button */}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-3 top-3 z-50 h-8 w-8 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
          </button>
          
          {/* Header */}
          <div className="px-6 py-5 border-b border-border bg-card text-center shrink-0">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-emerald-500/10 to-emerald-500/30 mb-4">
              <FontAwesomeIcon icon={faClipboardCheck} className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="text-3xl font-bold text-foreground">Risk Review</h2>
            <p className="mt-3 text-muted-foreground max-w-md mx-auto">
              Complete the review for this risk and set the next review date.
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-5 flex-1 min-h-0 overflow-y-auto space-y-4">
            {/* Risk Response (above name) */}
            {/* <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="risk-response" className="text-right text-foreground">
              Risk Response
            </Label>
            <div className="col-span-3">
              <Select value={riskResponse} onValueChange={setRiskResponse}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select response" />
                </SelectTrigger>
                <SelectContent>
                  {riskResponses.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div> */}
            {/* Name (expandable textarea) */}
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <Label className="block text-sm font-semibold text-foreground mb-3">
                Description
              </Label>
              <Textarea id="risk-name" value={selectedEvent?.title.replace('Review Risk: ', '') || ''} readOnly className="bg-background border-border resize-y min-h-[40px] max-h-[200px]" data-force-isms-override />
            </div>
            {/* Reviewed By (user dropdown) */}
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <Label className="block text-sm font-semibold text-foreground mb-3">
                Reviewed By
              </Label>
              <SingleUserCombobox
                selected={reviewedBy}
                setSelected={setReviewedBy}
                placeholder="Select reviewer..."
                className="bg-background border-border h-11"
              />
            </div>
            {/* Last Reviewed */}
            {/* <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="risk-prev-review-date" className="text-right text-foreground">
              Last Reviewed
            </Label>
            <div className="col-span-3">
              <DatePicker date={selectedEvent?.prevReviewDate ? new Date(selectedEvent.prevReviewDate) : undefined} setDate={() => { }} readOnly fullWidth placeholder="No date" />
            </div>
          </div> */}
            {/* Date Reviewed */}
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <Label className="block text-sm font-semibold text-foreground mb-3">
                Date Reviewed
              </Label>
              <DatePicker date={reviewDate} setDate={setReviewDate} fullWidth enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date" />
            </div>
            {/* Next Review */}
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <Label className="block text-sm font-semibold text-foreground mb-3">
                Next Review Date
              </Label>
              <DatePicker date={nextReviewDate} setDate={setNextReviewDate} fullWidth enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date" />
            </div>
            {/* Treatment Plan (conditional, textarea under next review) */}
            {riskResponse === 'treat' && (
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <Label className="block text-sm font-semibold text-foreground mb-3">
                  Treatment Plan
                </Label>
                <Textarea id="treatment-plan" value={treatmentPlan} onChange={e => setTreatmentPlan(e.target.value)} placeholder="Describe the treatment plan..." className="bg-background border-border min-h-[40px] max-h-[200px] resize-y" />
              </div>
            )}
            {/* Comments */}
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <Label className="block text-sm font-semibold text-foreground mb-1">
                Comments
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                Add any notes about this review
              </p>
              <Textarea id="comments" onChange={e => setComments(e.target.value)} placeholder="Add notes about the risk review..." className="bg-background border border-border min-h-[100px] focus:border-primary" />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-muted/30 shrink-0">
            <div className="flex items-center justify-between gap-4">
              <div className="text-xs text-muted-foreground truncate">
                {selectedEvent?.id && `ID: ${selectedEvent.id}`}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCompleteReview}
                  className="bg-primary hover:bg-primary/80 text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Complete Review
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewRiskDialog;

function autoIncrementVersion(version: string): string {
  const parts = version.split('.');
  let major = parseInt(parts[0]) || 1;
  let minor = parseInt(parts[1]) || 0;

  if (minor < 9) {
    minor += 1;
  } else {
    major += 1;
    minor = 0;
  }

  return `${major}.${minor}`;
}